import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import Trns from 'src/localization/Trns'
import { createSetIsDeleteAccountPopupOpen } from 'src/redux/actions/app.actions'
import {
  selectCreditCardsActiveDelegateCountStatisticsFromState,
  selectCreditCardsActiveSourceCountStatisticsFromState,
  selectVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import styled, { css } from 'styled-components'

interface DeleteProfileBoxProps {
  className?: string
  deletingUser: boolean
  topLevelError?: string
  deleteProfile: () => void
}

const StyledP = styled.p`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )}
  margin-top: 30px;
`

const DeleteProfileBox: React.FC<DeleteProfileBoxProps> = (
  props,
): React.ReactElement => {
  const dispatch = useDispatch()
  const activeAccounts = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const activeVCNs = useSelector(
    selectVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  )
  const activeBudgets = useSelector(
    selectCreditCardsActiveDelegateCountStatisticsFromState,
  )
  const canDelete = activeAccounts + activeVCNs + activeBudgets === 0

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        background-color: $color-gray-lightest;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            20,
            styleConstants.colors.red,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        <Trns source={'Delete Your Profile'} />
      </span>
      <StyledP>
        <Trns
          source={
            'In order to delete your ${appName} Profile, you must have had no virtual cards, budgets, or registered accounts ever connected to your profile.'
          }
          expressions={{
            appName: <AppName />,
          }}
        />
      </StyledP>
      {props.topLevelError !== undefined && (
        <span
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )}
            margin-top: 40px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
            `)}
          `}
        >
          {props.topLevelError}
        </span>
      )}

      {canDelete ? (
        <Button
          css={css`
            margin-top: 30px;
            align-self: flex-start;
          `}
          design={ButtonDesigns.designRed}
          type={ButtonTypes.button}
          text={<Trns source={'Permanently Delete Profile'} />}
          onClick={(): void => {
            dispatch(createSetIsDeleteAccountPopupOpen(true))
          }}
          loading={props.deletingUser}
          disabled={props.deletingUser}
        />
      ) : (
        <span
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.font_gray_light,
              styleConstants.fontFamilies.regular,
            )}
            margin-top: 33px;
          `}
        >
          <Trns
            source={
              'Please contact us via our ${resourceCenter} to complete the deletion of your account.'
            }
            expressions={{
              resourceCenter: (
                <ResourceCenterLink underline size={HoverableLinkSizes.Small} />
              ),
            }}
          />
        </span>
      )}
    </Box>
  )
}

export default DeleteProfileBox
